// The rebooking date-and-time step's enhancement
// (resources/views/rebooking/times.blade.php).
//
// The page asks in three states off ONE url, each a plain GET carrying the answers so far:
//
//     ?                              choose a date
//     ?date=2026-10-15               ... and a time for guest 1
//     ?date=...&times[1]=10:30       ... and a time for guest 2
//
// Without this file each choice is a form submit and a page load - two at most for the parties
// this journey handles, and every state a real, linkable, back-button-able page. This file
// fetches THE SAME URLS and swaps the region in, so there is no second code path deciding what
// is available: the server answers both, identically.
//
// THE REGION IS THE TIMES ONLY - the calendar is deliberately outside it. The dates on offer
// depend on the booking, not on which of them is chosen, so there is nothing to re-render; and
// re-rendering it breaks it, because availability-calendar.js collapses its month stack to one
// at a time by adding `.enhanced` on DOMContentLoaded and never runs again. A swapped-in copy
// would revert to the no-script stacked layout mid-journey. Keep the boundary where it is.
//
// That is the whole design. V1 has a bespoke JSON endpoint and a client-side renderer that
// builds slot markup by hand from the response - a second implementation of "what can this
// customer book", which is how its picker came to offer times its own submit would refuse.
// Here the browser renders nothing; it moves server-rendered HTML.

const FORM_SELECTOR = 'form.journey_form';
const REGION_SELECTOR = '[data-rebooking-region]';
const WATCHED = 'input[name="date"], input[name^="times["]';

let inFlight = null;

// The url for the state the form is now in. Built from the checked radios rather than from the
// current url, so it cannot drift from what the customer can see.
function urlFor(form) {
    const url = new URL(window.location.href);

    url.searchParams.delete('date');

    for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('times[')) {
            url.searchParams.delete(key);
        }
    }

    const date = form.querySelector('input[name="date"]:checked');

    if (! date) {
        return url;
    }

    url.searchParams.set('date', date.value);

    // Only up to the first gap. A later guest's answer without an earlier one is not a state
    // the page can produce, and the server drops it anyway - mirroring that here keeps the url
    // honest about what has been decided.
    const groups = [...form.querySelectorAll('fieldset')]
        .map(group => group.querySelector('input[name^="times["]'))
        .filter(Boolean);

    for (const first of groups) {
        const checked = form.querySelector(`input[name="${first.name}"]:checked`);

        if (! checked) {
            break;
        }

        url.searchParams.set(first.name, checked.value);
    }

    return url;
}

async function refresh(form) {
    const region = form.querySelector(REGION_SELECTOR);

    if (! region) {
        return;
    }

    const url = urlFor(form);

    // Latest choice wins. A customer clicking along a row of times fires several of these, and
    // an earlier reply arriving late would put back a state they have already moved past.
    const request = Symbol('request');
    inFlight = request;

    region.setAttribute('aria-busy', 'true');

    try {
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });

        if (! response.ok || inFlight !== request) {
            return;
        }

        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = parsed.querySelector(REGION_SELECTOR);

        if (! fresh) {
            return;
        }

        region.innerHTML = fresh.innerHTML;

        // The address bar follows, so a refresh or a back-button press lands on the state the
        // customer is looking at - which is the point of keeping the states in the url.
        window.history.replaceState({}, '', url);

        // The submit lives outside the swapped region (it is not part of what the server
        // re-renders there), so its presence is decided here from the same rule the Blade uses:
        // a time for every guest.
        syncSubmit(form, parsed);
    } catch {
        // A failed fetch leaves the page exactly as it was. The customer can still submit, and
        // the server re-checks everything regardless.
    } finally {
        if (inFlight === request) {
            region.removeAttribute('aria-busy');
            inFlight = null;
        }
    }
}

// Mirror the freshly rendered page's own submit button, rather than deciding when to show one.
function syncSubmit(form, parsed) {
    const current = form.querySelector('button[type="submit"]');
    const fresh = parsed.querySelector(`${FORM_SELECTOR} button[type="submit"]`);

    if (fresh && ! current) {
        form.appendChild(fresh.cloneNode(true));
    } else if (! fresh && current) {
        current.remove();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const form = document.querySelector(FORM_SELECTOR);

    if (! form || ! form.querySelector(REGION_SELECTOR)) {
        return;
    }

    // Delegated, because the controls inside the region are replaced on every refresh.
    form.addEventListener('change', event => {
        if (event.target.matches(WATCHED)) {
            refresh(form);
        }
    });
});
